/**
 * Visual Phone Layout Component
 * SVG visualization of phone with clickable line keys and expansion module
 */

import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import type { Hardware, LineKey, ExpansionKey } from './types';

interface VisualPhoneLayoutProps {
  hardware: Hardware;
  onButtonClick: (buttonNo: number | null, isExpansion: boolean) => void;
}

const TIER_SCALE = { compact: 1.0, mid: 1.2, high: 1.4 };

const VisualPhoneLayout: React.FC<VisualPhoneLayoutProps> = ({ hardware, onButtonClick }) => {
  // Get button layout from API data with fallbacks
  const leftPerPage = hardware.leftKeysPerPage ?? 0;
  const rightPerPage = hardware.rightKeysPerPage ?? 0;
  const pages = hardware.totalPages ?? 1;
  const tier = hardware.phoneTier ?? 'mid';
  const scale = TIER_SCALE[tier];

  // Determine if phone has physical buttons or is touchscreen/on-screen only
  const hasPhysicalButtons = leftPerPage > 0 || rightPerPage > 0;

  // Expanded mode for label display (more characters on mid/high tier phones)
  const expandedMode = tier !== 'compact';

  // Page state
  const [dssPage, setDssPage] = useState(1);
  const [expPage, setExpPage] = useState(1);

  // Calculate total pages
  const totalDssPages = pages;
  const totalExpPages = hardware.extModAvail ? 2 : 0; // 40 keys = 2 pages of 20

  // Handle page switching
  const handleDssPageSwitch = () => {
    setDssPage((prev) => (prev % totalDssPages) + 1);
  };

  const handleExpPageSwitch = () => {
    setExpPage((prev) => (prev % totalExpPages) + 1);
  };

  // Geometry calculations
  const body = { x: 80, y: 120, w: 1040, h: 720 };
  const screen = {
    x: 160,
    y: 200,
    w: Math.min(650 * scale, body.w - 260),
    h: Math.min(300 * scale, body.h - 260),
  };

  // Expansion panel dimensions
  const expPanelX = screen.x + screen.w + 40 + 46 + 160; // keyW = 46
  const expPanelY = 120;
  const expPanelW = 600; // Increased from 460
  const expPanelH = 760;

  // Get line keys and expansion keys
  const lineKeys = hardware.lineKeys || [];
  const expansionKeys = hardware.expansionKeys || [];

  // Calculate button range for current DSS page
  const keysPerPage = leftPerPage + rightPerPage;
  const dssBaseIndex = (dssPage - 1) * keysPerPage;

  // Get max characters based on expanded mode capability
  const maxChars = expandedMode ? 10 : 6;

  // Helper to truncate label
  const truncateLabel = (label: string): string => {
    return label.length > maxChars ? label.substring(0, maxChars) : label;
  };

  // Helper to get label for a button
  const getButtonLabel = (buttonNo: number): string => {
    const key = lineKeys.find(k => k.LineKeyNo === buttonNo);
    if (!key) return truncateLabel(`Button ${buttonNo}`);

    const typeStr = String(key['linekey.type']);
    let label = '';
    if (typeStr === '13') label = key['linekey.label'] || key['linekey.value'] || `Btn ${buttonNo}`;
    else if (typeStr === '15') label = key.Extension ? String(key.Extension) : `Btn ${buttonNo}`;
    else if (typeStr === '16') label = key['linekey.label'] || (key.Extension ? String(key.Extension) : `Btn ${buttonNo}`);
    else label = `Button ${buttonNo}`;

    return truncateLabel(label);
  };

  const getExpansionLabel = (keyNo: number): string => {
    const key = expansionKeys.find(k => k.KeyNo === keyNo);
    if (!key) return truncateLabel(`E${keyNo}`);

    const typeStr = String(key['expansion_module.key.type']);
    let label = '';
    if (typeStr === '13') label = key['expansion_module.key.label'] || key['expansion_module.key.value'] || `E${keyNo}`;
    else if (typeStr === '15') label = key.Extension ? String(key.Extension) : `E${keyNo}`;
    else if (typeStr === '16') label = key['expansion_module.key.label'] || (key.Extension ? String(key.Extension) : `E${keyNo}`);
    else label = `E${keyNo}`;

    return truncateLabel(label);
  };

  // Calculate vertical positions for keys
  const calculateVerticalPositions = (count: number) => {
    const boxH = 46;
    const top = 25;
    const bottom = 25;
    if (count <= 1) {
      return [{ labelY: screen.y + (screen.h - boxH) / 2, cy: screen.y + screen.h / 2 }];
    }
    const free = screen.h - top - bottom - count * boxH;
    const gap = Math.max(10, free / (count - 1));
    return Array.from({ length: count }, (_, i) => {
      const labelY = screen.y + top + i * (boxH + gap);
      return { labelY, cy: labelY + boxH / 2 };
    });
  };

  const leftPositions = calculateVerticalPositions(leftPerPage);
  const rightPositions = calculateVerticalPositions(rightPerPage);

  const keyW = 46;
  const keyH = 60;

  // If no physical buttons but has expansion, show message + expansion rendering
  // If no physical buttons and no expansion, show just the message
  const showTouchscreenMessage = !hasPhysicalButtons;

  return (
    <Box sx={{ width: '100%', height: 'auto', overflow: 'auto' }}>
      {/* Info message for touchscreen models */}
      {showTouchscreenMessage && (
        <Box sx={{ p: 2, mb: 2, bgcolor: '#f0f7ff', borderRadius: 2, border: '1px solid #b3d7ff' }}>
          <Typography variant="subtitle1" fontWeight="600" gutterBottom>
            {hardware.model} - Touchscreen DSS Keys
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This phone uses on-screen DSS keys displayed on the touchscreen ({hardware.linekeys || 0} keys).
            Line keys are configured through the phone's display interface.
          </Typography>
          {hardware.extModAvail && (
            <Typography variant="body2" color="primary">
              ✓ Expansion module shown below for additional physical keys
            </Typography>
          )}
        </Box>
      )}

      {/* Only show SVG if there are physical buttons OR expansion module */}
      {(hasPhysicalButtons || hardware.extModAvail) && (
        <svg viewBox="0 0 2100 1000" style={{ width: '100%', height: 'auto', display: 'block' }}>
          {/* Phone body - only show if has physical buttons */}
          {hasPhysicalButtons && (
            <>
              <rect
                x={body.x}
                y={body.y}
                rx="30"
                ry="30"
                width={body.w}
                height={body.h}
                fill="#fff"
                stroke="#3c3c3c"
                strokeWidth="3"
              />

              {/* Screen */}
              <rect
                x={screen.x}
                y={screen.y}
                rx="20"
                ry="20"
                width={screen.w}
                height={screen.h}
                fill="#fff"
                stroke="#3c3c3c"
                strokeWidth="3"
              />
            </>
          )}

        {/* DSS Page Switch Button (below screen, like soft key) - only for physical buttons */}
        {hasPhysicalButtons && totalDssPages > 1 && (
          <g style={{ cursor: 'pointer' }} onClick={handleDssPageSwitch}>
            <rect
              x={screen.x + 20}
              y={screen.y + screen.h + 30}
              rx="10"
              ry="10"
              width="160"
              height="38"
              fill="#e6e6e6"
              stroke="#3c3c3c"
              strokeWidth="2"
            />
            <text
              x={screen.x + 100}
              y={screen.y + screen.h + 54}
              fontSize="18"
              fill="#333"
              textAnchor="middle"
              fontWeight="600"
            >
              Switch Page {dssPage}/{totalDssPages}
            </text>
          </g>
        )}

        {/* Left DSS keys - only for physical buttons */}
        {hasPhysicalButtons && Array.from({ length: leftPerPage }, (_, i) => {
          const btnNo = dssBaseIndex + i + 1;
          const keyX = screen.x - 40 - keyW;
          const pos = leftPositions[i];
          const keyY = pos.cy - keyH / 2;
          const labelX = screen.x + 20;
          const labelY = pos.labelY;
          const label = getButtonLabel(btnNo);

          return (
            <g key={`left-${btnNo}`}>
              {/* Physical button */}
              <rect
                x={keyX}
                y={keyY}
                rx="8"
                ry="8"
                width={keyW}
                height={keyH}
                fill="#f5f5f5"
                stroke="#3c3c3c"
                strokeWidth="2"
                style={{ cursor: 'pointer' }}
                onClick={() => onButtonClick(btnNo, false)}
              >
                <title>Configure Button {btnNo}</title>
              </rect>
              {/* Button number badge */}
              <circle cx={keyX - 25} cy={pos.cy} r="16" fill="#fff" stroke="#111827" strokeWidth="2" />
              <text
                x={keyX - 25}
                y={pos.cy + 5}
                fontSize="14"
                fill="#111827"
                textAnchor="middle"
              >
                {btnNo}
              </text>
              {/* Label on screen */}
              <rect
                x={labelX}
                y={labelY}
                rx="8"
                ry="8"
                width="210"
                height="46"
                fill="#fff"
                stroke="#d2d2d2"
                strokeWidth="2"
              />
              <text
                x={labelX + 12}
                y={labelY + 28}
                fontSize="22"
                fill="#111"
              >
                {label}
              </text>
            </g>
          );
        })}

        {/* Right DSS keys - only for physical buttons */}
        {hasPhysicalButtons && Array.from({ length: rightPerPage }, (_, i) => {
          const btnNo = dssBaseIndex + leftPerPage + i + 1;
          const keyX = screen.x + screen.w + 40;
          const pos = rightPositions[i];
          const keyY = pos.cy - keyH / 2;
          const labelX = screen.x + screen.w - 220;
          const labelY = pos.labelY;
          const label = getButtonLabel(btnNo);

          return (
            <g key={`right-${btnNo}`}>
              {/* Physical button */}
              <rect
                x={keyX}
                y={keyY}
                rx="8"
                ry="8"
                width={keyW}
                height={keyH}
                fill="#f5f5f5"
                stroke="#3c3c3c"
                strokeWidth="2"
                style={{ cursor: 'pointer' }}
                onClick={() => onButtonClick(btnNo, false)}
              >
                <title>Configure Button {btnNo}</title>
              </rect>
              {/* Button number badge */}
              <circle cx={keyX + keyW + 25} cy={pos.cy} r="16" fill="#fff" stroke="#111827" strokeWidth="2" />
              <text
                x={keyX + keyW + 25}
                y={pos.cy + 5}
                fontSize="14"
                fill="#111827"
                textAnchor="middle"
              >
                {btnNo}
              </text>
              {/* Label on screen */}
              <rect
                x={labelX}
                y={labelY}
                rx="8"
                ry="8"
                width="210"
                height="46"
                fill="#fff"
                stroke="#d2d2d2"
                strokeWidth="2"
              />
              <text
                x={labelX + 12}
                y={labelY + 28}
                fontSize="22"
                fill="#111"
              >
                {label}
              </text>
            </g>
          );
        })}

        {/* Expansion Module */}
        {hardware.extModAvail && (
          <g>
            {/* Expansion panel */}
            <rect
              x={expPanelX}
              y={expPanelY}
              rx="18"
              ry="18"
              width={expPanelW}
              height={expPanelH}
              fill="#fff"
              stroke="#3c3c3c"
              strokeWidth="3"
            />
            <text
              x={expPanelX + 16}
              y={expPanelY + 30}
              fontSize="18"
              fill="#111"
              fontWeight="600"
            >
              Expansion Module - Page {expPage}/2
            </text>

            {/* Expansion keys - 20 per page */}
            {Array.from({ length: 10 }, (_, row) => {
              const topPad = 70;
              const rowPad = 10;
              const btnH = 48;
              const btnW = 90;
              const y = expPanelY + topPad + rowPad + row * (btnH + rowPad);

              // Calculate keys for current expansion page
              const expBaseIndex = (expPage - 1) * 20;
              const leftKey = expBaseIndex + row + 1;
              const rightKey = expBaseIndex + 10 + row + 1;

              const colLeftX = expPanelX + 18;
              const colRightX = expPanelX + expPanelW - 18 - btnW;

              const screenX = colLeftX + btnW + 16;
              const screenW = colRightX - 16 - screenX;
              const screenY = expPanelY + 50;
              const screenH = expPanelH - 50 - 56;

              const leftLabel = getExpansionLabel(leftKey);
              const rightLabel = getExpansionLabel(rightKey);

              return (
                <g key={`exp-row-${row}`}>
                  {/* Left button */}
                  <rect
                    x={colLeftX}
                    y={y}
                    rx="8"
                    ry="8"
                    width={btnW}
                    height={btnH}
                    fill="#f5f5f5"
                    stroke="#3c3c3c"
                    strokeWidth="2"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onButtonClick(leftKey, true)}
                  >
                    <title>Configure Expansion Key {leftKey}</title>
                  </rect>
                  {/* Left button number */}
                  <text
                    x={colLeftX + btnW / 2}
                    y={y + btnH / 2 + 6}
                    fontSize="16"
                    fill="#666"
                    textAnchor="middle"
                    fontWeight="600"
                  >
                    {leftKey}
                  </text>

                  {/* Right button */}
                  <rect
                    x={colRightX}
                    y={y}
                    rx="8"
                    ry="8"
                    width={btnW}
                    height={btnH}
                    fill="#f5f5f5"
                    stroke="#3c3c3c"
                    strokeWidth="2"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onButtonClick(rightKey, true)}
                  >
                    <title>Configure Expansion Key {rightKey}</title>
                  </rect>
                  {/* Right button number */}
                  <text
                    x={colRightX + btnW / 2}
                    y={y + btnH / 2 + 6}
                    fontSize="16"
                    fill="#666"
                    textAnchor="middle"
                    fontWeight="600"
                  >
                    {rightKey}
                  </text>

                  {/* Screen labels - only render on first row to avoid overlap */}
                  {row === 0 && (
                    <rect
                      x={screenX}
                      y={screenY}
                      rx="12"
                      ry="12"
                      width={screenW}
                      height={screenH}
                      fill="#fff"
                      stroke="#3c3c3c"
                      strokeWidth="3"
                    />
                  )}

                  {/* Labels on center screen */}
                  <text
                    x={screenX + 14}
                    y={y + 28}
                    fontSize="16"
                    fill="#111"
                  >
                    {leftLabel}
                  </text>
                  <text
                    x={screenX + screenW - 14}
                    y={y + 28}
                    fontSize="16"
                    fill="#111"
                    textAnchor="end"
                  >
                    {rightLabel}
                  </text>
                </g>
              );
            })}

            {/* EXP Switch Button (at bottom of expansion panel) */}
            {totalExpPages > 1 && (
              <g style={{ cursor: 'pointer' }} onClick={handleExpPageSwitch}>
                <rect
                  x={expPanelX + (expPanelW - 140) / 2}
                  y={expPanelY + expPanelH - 36 - 16}
                  rx="10"
                  ry="10"
                  width="140"
                  height="36"
                  fill="#e6e6e6"
                  stroke="#3c3c3c"
                  strokeWidth="2"
                />
                <text
                  x={expPanelX + expPanelW / 2}
                  y={expPanelY + expPanelH - 36 - 16 + 24}
                  fontSize="16"
                  fill="#333"
                  textAnchor="middle"
                  fontWeight="600"
                >
                  EXP Switch {expPage}/2
                </text>
              </g>
            )}
          </g>
        )}
        </svg>
      )}
    </Box>
  );
};

export default VisualPhoneLayout;
